import isEqual from 'lodash/isEqual';
import { DtFilterValue } from '@/components/data-table/helpers';

type SavedFilterSnapshot = {
  values: DtFilterValue[];
  keyword: string;
};

export type SavedFilterItem = {
  id: string;
  name: string;
  values: DtFilterValue[];
  isPreset?: boolean;
};

// Reduce a filter value to a comparable form: extract `.value` from UiOption-like
// wrappers (objects with both `value` and `label`) so a preset's `string[]` matches
// the UI's `UiOption[]`, and sort string arrays so user selection order doesn't
// matter. Non-string arrays (e.g. DateRangeValue `[start, end]` or `number[]`) keep
// their order because position is meaningful. lodash.isEqual handles Date comparison
// natively, so date-range values pass through unchanged.
const normalizeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const mapped = (value as unknown[]).map(normalizeValue);
    return mapped.every((val) => typeof val === 'string') ? mapped.sort() : mapped;
  }
  if (value && typeof value === 'object' && 'value' in value && 'label' in value) {
    return String((value as { value: unknown }).value);
  }
  return value;
};

const normalizeSnapshot = (snapshot: SavedFilterSnapshot) => ({
  keyword: snapshot.keyword || '',
  values: snapshot.values
    .map(({ columnName, value }) => ({ columnName, value: normalizeValue(value) }))
    .sort((a, b) => a.columnName.localeCompare(b.columnName)),
});

export const isSavedFilterMatch = (
  savedFilter: SavedFilterItem,
  snapshot: SavedFilterSnapshot
): boolean =>
  isEqual(
    normalizeSnapshot({ values: savedFilter.values, keyword: '' }),
    normalizeSnapshot(snapshot)
  );
